import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, Users, Mail, Star, Clock, CheckCircle, Crown, Eye, FileText, UserX, Grid, List, MoreHorizontal, Calendar, Settings } from 'lucide-react';
import { useTeamStore, TeamMember } from '../../store/teamStore';
import { useAuthStore } from '../../store/authStore';
import InviteMemberModal from './InviteMemberModal';
import ViewMemberModal from './ViewMemberModal';
import RolePermissionsModal from './RolePermissionsModal';

const TeamManagementPage: React.FC = () => {
  const { user } = useAuthStore();
  const {
    teamMembers,
    invitations,
    rolePermissions,
    isLoading,
    searchTerm,
    filterRole,
    setSearchTerm,
    setFilterRole,
    fetchTeamMembers,
    fetchInvitations,
    fetchRolePermissions,
    deleteMember,
    deleteInvitation,
    resendInvitation
  } = useTeamStore();

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [viewingMember, setViewingMember] = useState<TeamMember | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');

  useEffect(() => {
    fetchTeamMembers();
    fetchInvitations();
    fetchRolePermissions();
  }, [fetchTeamMembers, fetchInvitations, fetchRolePermissions]);

  const roles = ['Admin', 'Editor', 'Viewer', 'Analyst'];

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'Owner': return <Star className="w-4 h-4 text-gray-400" />;
      case 'Admin': return <Crown className="w-4 h-4 text-gray-400" />;
      case 'Editor': return <Edit className="w-4 h-4 text-gray-400" />;
      case 'Viewer': return <Eye className="w-4 h-4 text-gray-400" />;
      case 'Analyst': return <FileText className="w-4 h-4 text-gray-400" />;
      default: return <Users className="w-4 h-4 text-gray-400" />;
    }
  };

  const getRoleColor = (role: string) => {
    const colors: Record<string, string> = {
      'Owner': 'from-orange-400 to-orange-600 text-white',
      'Admin': 'from-yellow-400 to-yellow-600 text-white',
      'Editor': 'from-primary-400 to-primary-600 text-white',
      'Viewer': 'from-green-400 to-green-600 text-white',
      'Analyst': 'from-purple-400 to-purple-600 text-white'
    };
    return colors[role] || 'from-gray-400 to-gray-500 text-white';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Online': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'Offline': return <Clock className="w-4 h-4 text-gray-400" />;
      case 'Away': return <Clock className="w-4 h-4 text-yellow-500" />;
      default: return <UserX className="w-4 h-4 text-gray-400" />;
    }
  };

  const hasPermission = (permission: string) => {
    if (!user?.role) return false;
    if (['Admin', 'Owner'].includes(user.role)) return true;
    return rolePermissions[user.role]?.includes(permission) || false;
  };

  const handleEditMember = (member: TeamMember) => {
    setSelectedMember(member);
    setShowInviteModal(true);
  };

  const handleCloseModal = () => {
    setShowInviteModal(false);
    setSelectedMember(null);
  };

  const handleViewMember = (member: TeamMember) => {
    setViewingMember(member);
  };

  const filteredMembers = teamMembers.filter(member => {
    const matchesSearch = member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || member.role === filterRole;
    return matchesSearch && matchesRole;
  });

  const roleCounts = teamMembers.reduce((acc, member) => {
    acc[member.role] = (acc[member.role] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const renderListView = () => (
    <div className="space-y-4">
      {filteredMembers.map(member => (
        <div key={member.id} className="group bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 hover:shadow-[0_0_20px_rgba(0,0,0,0.1)] transition-all duration-200 overflow-hidden p-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="w-12 h-12 bg-primary-600 rounded-lg flex items-center justify-center text-white font-semibold text-lg shadow-lg">
                {member.name.charAt(0)}
              </div>
              <div className="absolute -bottom-1 -right-1">
                {getStatusIcon(member.status)}
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-gray-900 dark:text-white truncate">{member.name}</h3>
                {member.isOwner && <Crown className="w-4 h-4 text-yellow-500 flex-shrink-0" />}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{member.email}</p>
              <div className="flex items-center gap-3 mt-2">
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gradient-to-r ${getRoleColor(member.role)} shadow-sm`}>
                  {getRoleIcon(member.role)}
                  <span className="ml-1">{member.role}</span>
                </span>
                <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                  <span className="capitalize">{member.status}</span>
                </div>
              </div>
            </div>
          </div>

          {!member.isOwner && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => handleViewMember(member)}
                className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200"
                title="View Details"
              >
                <Eye className="w-4 h-4" />
              </button>
              {hasPermission('manage_roles') && (
                <button
                  onClick={() => handleEditMember(member)}
                  className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all duration-200"
                  title="Edit Member"
                >
                  <Edit className="w-4 h-4" />
                </button>
              )}
              {hasPermission('remove_member') && (
                <button
                  onClick={() => deleteMember(member.id)}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
                  title="Remove Member"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  const renderGridView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {filteredMembers.map(member => (
        <div key={member.id} className="group bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 hover:shadow-[0_0_20px_rgba(0,0,0,0.1)] transition-all duration-200 overflow-hidden">
          <div className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="w-12 h-12 bg-primary-600 rounded-lg flex items-center justify-center text-white font-semibold text-lg shadow-lg">
                    {member.name.charAt(0)}
                  </div>
                  {member.isOwner && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-yellow-500 rounded-full flex items-center justify-center border-2 border-white">
                      <Crown className="w-3 h-3 text-white" />
                    </div>
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors truncate max-w-[150px]">
                    {member.name}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 truncate max-w-[180px]">{member.email}</p>
                </div>
              </div>
              <button className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                <MoreHorizontal className="w-4 h-4 text-gray-400 dark:text-gray-500" />
              </button>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r ${getRoleColor(member.role)} shadow-sm`}>
                  {getRoleIcon(member.role)}
                  <span className="ml-1">{member.role}</span>
                </span>
                <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                  {getStatusIcon(member.status)}
                  <span className="capitalize">{member.status}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-100 dark:border-gray-600">
            <div className="flex items-center justify-between">
              {!member.isOwner ? (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleViewMember(member)}
                    className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all"
                    title="View Details"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  {hasPermission('manage_roles') && (
                    <button
                      onClick={() => handleEditMember(member)}
                      className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all"
                      title="Edit Member"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                  )}
                  {hasPermission('remove_member') && (
                    <button
                      onClick={() => deleteMember(member.id)}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                      title="Remove Member"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ) : (
                <div className="h-7"></div>
              )}
              {member.joinedAt && (
                <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                  <Calendar className="w-3 h-3" />
                  <span>{new Date(member.joinedAt).toLocaleDateString()}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Team Management</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Manage your team members and their permissions</p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-4 gap-4 w-full max-w-2xl">
          <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 flex flex-col items-center space-y-2 min-w-0">
            <div className="text-sm text-gray-600 dark:text-gray-400">Total Members</div>
            <div className="text-xl font-bold text-gray-900 dark:text-white text-center break-all">
              {teamMembers.length}
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 flex flex-col items-center space-y-2 min-w-0">
            <div className="text-sm text-gray-600 dark:text-gray-400">Admins</div>
            <div className="text-xl font-bold text-yellow-600 text-center break-all">
              {roleCounts['Admin'] || 0}
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 flex flex-col items-center space-y-2 min-w-0">
            <div className="text-sm text-gray-600 dark:text-gray-400">Editors</div>
            <div className="text-xl font-bold text-primary-600 dark:text-primary-400 text-center break-all">
              {roleCounts['Editor'] || 0}
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 flex flex-col items-center space-y-2 min-w-0">
            <div className="text-sm text-gray-600 dark:text-gray-400">Pending</div>
            <div className="text-xl font-bold text-orange-600 dark:text-orange-400 text-center break-all">
              {invitations.length}
            </div>
          </div>
        </div>
      </div>

      {/* Filters & Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                placeholder="Search team members..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 focus:bg-white dark:focus:bg-gray-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-colors"
              />
            </div>
            <div className="flex gap-2 min-w-0">
              <select
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                className="px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 focus:bg-white dark:focus:bg-gray-600 text-gray-900 dark:text-gray-100 transition-colors min-w-[150px] max-w-full"
              >
                <option value="all">All Roles</option>
                {roles.map(role => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'list' ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
              >
                <List className="w-4 h-4" />
                List
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'grid' ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
              >
                <Grid className="w-4 h-4" />
                Grid
              </button>
            </div>
            {hasPermission('invite_member') && (
              <button
                onClick={() => setShowInviteModal(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <Plus className="w-4 h-4" />
                Invite Member
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Roles Sidebar */}
        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Roles</h2>
            {hasPermission('manage_roles') && (
              <button
                onClick={() => setShowRoleModal(true)}
                className="p-1.5 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                title="Manage Roles"
              >
                <Settings className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="space-y-4">
            {roles.map(role => (
              <div key={role} className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                <div className="flex items-center gap-2">
                  {getRoleIcon(role)}
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{role}</span>
                </div>
                <span className="text-sm text-gray-500 dark:text-gray-400">● {roleCounts[role] || 0}</span>
              </div>
            ))}
            <div className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Pending</span>
              </div>
              <span className="text-sm text-gray-500 dark:text-gray-400">● {invitations.length}</span>
            </div>
          </div>
        </div>

        {/* Team Members Content */}
        <div className="lg:col-span-3 space-y-6">
          {/* Team Members List/Grid */}
          <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Team Members</h2>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {filteredMembers.length} member{filteredMembers.length !== 1 ? 's' : ''} found
              </span>
            </div>

            {filteredMembers.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No team members found</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  {searchTerm || filterRole !== 'all'
                    ? 'Try adjusting your search or filters'
                    : 'Get started by inviting your first team member'
                  }
                </p>
                {!searchTerm && filterRole === 'all' && hasPermission('invite_member') && (
                  <button
                    onClick={() => setShowInviteModal(true)}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Invite Your First Member
                  </button>
                )}
              </div>
            ) : (
              viewMode === 'list' ? renderListView() : renderGridView()
            )}
          </div>

          {/* Pending Invitations */}
          {invitations.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Pending Invitations</h2>
              <div className="space-y-4">
                {invitations.map(invitation => (
                  <div key={invitation.id} className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
                        <Mail className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{invitation.email}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gradient-to-r ${getRoleColor(invitation.role)}`}>
                            {getRoleIcon(invitation.role)}
                            <span className="ml-1">{invitation.role}</span>
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            Invited {new Date(invitation.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {hasPermission('invite_member') && (
                        <button
                          onClick={() => resendInvitation(invitation.id)}
                          className="px-3 py-1 text-sm text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                        >
                          Resend
                        </button>
                      )}
                      {hasPermission('invite_member') && (
                        <button
                          onClick={() => deleteInvitation(invitation.id)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Invite Member Modal */}
      {showInviteModal && (
        <InviteMemberModal
          isOpen={showInviteModal}
          onClose={handleCloseModal}
          member={selectedMember}
        />
      )}

      {/* View Member Modal */}
      {viewingMember && (
        <ViewMemberModal
          isOpen={!!viewingMember}
          onClose={() => setViewingMember(null)}
          member={viewingMember}
          onEdit={() => {
            handleEditMember(viewingMember!);
            setViewingMember(null);
          }}
        />
      )}

      {/* Role Permissions Modal */}
      <RolePermissionsModal
        isOpen={showRoleModal}
        onClose={() => setShowRoleModal(false)}
      />
    </div>
  );
};

export default TeamManagementPage;